import { FC, useRef, useMemo } from 'react';
import Map, { Marker, MapRef, GeolocateControl } from 'react-map-gl/mapbox';
import { Stop } from "@city-sage/core-client";
import { MapPin } from 'lucide-react';
import { MAPBOX_ACCESS_TOKEN } from '../../lib/map/token';
import 'mapbox-gl/dist/mapbox-gl.css';

interface StopMapProps {
  stop: Stop;
  height?: string;
  className?: string;
}

export const StopMap: FC<StopMapProps> = ({ 
  stop, 
  height = "h-48", 
  className = "" 
}) => {
  const mapRef = useRef<MapRef | null>(null);

  // Check if stop has valid coordinates
  const hasValidCoordinates = useMemo(() => {
    return stop.place.coordinates?.latitude && 
           stop.place.coordinates?.longitude &&
           !isNaN(stop.place.coordinates.latitude) &&
           !isNaN(stop.place.coordinates.longitude);
  }, [stop.place.coordinates]);

  // Calculate initial view state
  const initialViewState = useMemo(() => {
    if (hasValidCoordinates) {
      return {
        longitude: stop.place.coordinates?.longitude,
        latitude: stop.place.coordinates?.latitude,
        zoom: 15
      };
    }
    
    return {
      longitude: 0,
      latitude: 20,
      zoom: 1
    };
  }, [hasValidCoordinates, stop.place.coordinates]);

  // If no valid coordinates, show placeholder
  if (!hasValidCoordinates) {
    return (
      <div className={`${height} border-2 border-dashed border-gray-300 bg-gray-50 rounded-lg flex items-center justify-center ${className}`}>
        <div className="text-center text-gray-500">
          <MapPin className="h-8 w-8 mx-auto mb-2" />
          <p className="font-medium">Location not available</p>
          <p className="text-xs mt-1">Coordinates missing</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${height} rounded-lg overflow-hidden border border-gray-200 relative ${className}`}>
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        initialViewState={initialViewState}
        attributionControl={false}
        logoPosition="bottom-right"
      >
        {/* Stop marker */}
        <Marker
          longitude={stop.place.coordinates?.longitude}
          latitude={stop.place.coordinates?.latitude}
          anchor="bottom"
        >
          <div className="relative">
            {/* Marker pin */}
            <div className="w-8 h-8 bg-blue-600 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
              <MapPin className="h-4 w-4 text-white" />
            </div>
            {/* Index badge */}
            <div className="absolute -top-2 -right-2 w-5 h-5 bg-white border-2 border-blue-600 rounded-full flex items-center justify-center shadow-md">
              <span className="text-xs font-bold text-blue-600">{stop.index}</span>
            </div>
            {/* Marker shadow/pulse effect */}
            <div className="absolute inset-0 w-8 h-8 bg-blue-600 rounded-full animate-ping opacity-20"></div>
          </div>
        </Marker>

        {/* Geolocation Control */}
        <GeolocateControl
          position="top-right"
          positionOptions={{ enableHighAccuracy: true }}
          trackUserLocation={true}
          showAccuracyCircle={false}
        />
      </Map>
    </div>
  );
}; 